import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastType } from 'react-toastify';

import type {
  BlackbaudRaisersEdgeIntegration,
  IBlackbaudRaisersEdgeIntegrationAttributes,
} from '@feathr/blackbox';
import { Button, Card, EFontAwesomeKitIcon, FontAwesomeKitIcon, toast } from '@feathr/components';
import { StoresContext } from '@feathr/extender/state';

import * as styles from './RaisersEdgeConnectBlackbaudCard.css';

type TBlackbaudLoginUrl = IBlackbaudRaisersEdgeIntegrationAttributes['login_url'] | undefined;

interface IProps {
  integration?: BlackbaudRaisersEdgeIntegration;
}

function RaisersEdgeConnectBlackbaudCard({ integration }: IProps): JSX.Element {
  const { t } = useTranslation();
  const { BlackbaudRaisersEdgeIntegrations } = useContext(StoresContext);
  const loginUrl: TBlackbaudLoginUrl = integration?.get('login_url');

  async function handleOnClick(): Promise<void> {
    if (!integration) {
      try {
        const newIntegration = BlackbaudRaisersEdgeIntegrations.create();
        // Setting validate to false because we don't want to validate the integration until the end.
        const returnedIntegration = await BlackbaudRaisersEdgeIntegrations.add(newIntegration, {
          validate: false,
        });
        // Used when we add a new model, so that it would force refresh collection.list() that could include that new model, if it had previously existed.
        BlackbaudRaisersEdgeIntegrations.clearApiCache();
        // Use the login_url from the new integration to redirect the user to the Blackbaud login page.
        const newLoginUrl: TBlackbaudLoginUrl = returnedIntegration.get('login_url');

        if (newLoginUrl) {
          window.location.assign(newLoginUrl);
        } else {
          toast(
            t('There was an error getting the login URL. Please refresh the page and try again.'),
            {
              type: ToastType.ERROR,
            },
          );
        }
      } catch (error) {
        toast(t('There was an error creating an integration. Please try again.'), {
          type: ToastType.ERROR,
        });
      }
    }
  }

  return (
    <Card className={styles.root} contentClassName={styles.cardContent}>
      <div>
        <h3>{t('Connect Blackbaud Account')}</h3>
        <p>{t("Use your Blackbaud admin credentials to connect Raiser's Edge NXT to Feathr.")}</p>
      </div>
      <Button
        href={loginUrl}
        isLoading={integration?.isPending}
        onClick={loginUrl ? undefined : handleOnClick}
        prefix={<FontAwesomeKitIcon iconName={EFontAwesomeKitIcon.blackbaud} />}
      >
        {t('Connect Blackbaud')}
      </Button>
    </Card>
  );
}

export default observer(RaisersEdgeConnectBlackbaudCard);
